import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Badge, Button, Modal, Form } from 'react-bootstrap';
import { battlesApi } from '../../api/battles';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import HrnbSelect from '../../components/controls/HrnbSelect';
import AdminPagination from '../components/pagination/AdminPagination';
import { getFormatIcon } from '../../utils/battleFormats';
import './AdminBattles.css';

const AdminBattles = () => {
    const [battles, setBattles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedBattle, setSelectedBattle] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // Пагинация
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    // Фильтры
    const [filterFormat, setFilterFormat] = useState('');

    // Загрузка баттлов
    const loadBattles = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = {
                page: currentPage - 1,
                size: pageSize,
                ...(filterFormat && { format: filterFormat })
            };
            const data = await battlesApi.list(params);

            let battlesList = [];
            let total = 0;

            if (Array.isArray(data)) {
                battlesList = data;
                total = data.length;
            } else if (data && Array.isArray(data.content)) {
                battlesList = data.content;
                total = data.totalElements || data.total || data.content.length;
            }

            setBattles(battlesList);
            setTotalItems(total);
        } catch (err) {
            setError('Не удалось загрузить список баттлов');
            console.error('[AdminBattles] Failed to load battles:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filterFormat]);

    useEffect(() => {
        loadBattles();
    }, [loadBattles]);

    // Получение цвета бейджа статуса
    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'PLANNED':
                return 'info';
            case 'ACTIVE':
                return 'success';
            case 'FINISHED':
                return 'secondary';
            case 'CANCELLED':
                return 'danger';
            default:
                return 'warning';
        }
    };

    // Получение цвета бейджа типа
    const getTypeBadgeVariant = (type) => {
        switch (type) {
            case 'SINGLE':
                return 'primary';
            case 'TEAM':
                return 'success';
            default:
                return 'secondary';
        }
    };

    // Получение цвета бейджа формата
    const getFormatBadgeVariant = (format) => {
        switch (format) {
            case 'AUDIO':
                return 'info';
            case 'VIDEO':
                return 'purple';
            case 'TEXT':
                return 'warning';
            default:
                return 'secondary';
        }
    };

    // Получение цвета бейджа видимости
    const getVisibilityBadgeVariant = (visibility) => {
        switch (visibility) {
            case 'PUBLIC':
                return 'success';
            case 'PRIVATE':
                return 'warning';
            case 'INVITE_ONLY':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    // Открытие модального окна смены статуса
    const handleStatusChange = (battle) => {
        setSelectedBattle(battle);
        setNewStatus(battle.status);
        setShowStatusModal(true);
    };

    // Сохранение нового статуса
    const saveStatus = async () => {
        if (!selectedBattle) return;

        try {
            setActionLoading(true);
            await battlesApi.updateStatus(selectedBattle.id, newStatus);
            await loadBattles();
            setShowStatusModal(false);
            setSelectedBattle(null);
        } catch (err) {
            setError(`Не удалось изменить статус баттла: ${err.message}`);
            console.error('[AdminBattles] Failed to update status:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Удаление баттла
    const handleDeleteBattle = async (battle) => {
        if (!window.confirm(`Вы уверены, что хотите удалить баттл "${battle.title}"?`)) {
            return;
        }

        try {
            setActionLoading(true);
            await battlesApi.remove(battle.id);
            await loadBattles();
        } catch (err) {
            setError(`Не удалось удалить баттл: ${err.message}`);
            console.error('[AdminBattles] Failed to delete battle:', err);
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">Управление баттлами</h1>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <HrnbCard>
                <div className="admin-pagination-header">
                    <div className="admin-filters-row" style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                        <Form.Label style={{ marginBottom: 0, fontWeight: 500 }}>Фильтр по формату:</Form.Label>
                        <Form.Select
                            className="admin-format-filter"
                            style={{ maxWidth: 200 }}
                            value={filterFormat}
                            onChange={(e) => {
                                setFilterFormat(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">Все форматы</option>
                            <option value="AUDIO">🎵 Аудио</option>
                            <option value="TEXT">📝 Текст</option>
                        </Form.Select>
                        {filterFormat && (
                            <Button variant="outline" size="sm" onClick={() => setFilterFormat('')}>
                                Сбросить
                            </Button>
                        )}
                    </div>
                    <Form.Select
                        className="admin-page-size-selector"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={5}>5 на странице</option>
                        <option value={10}>10 на странице</option>
                        <option value={25}>25 на странице</option>
                        <option value={50}>50 на странице</option>
                    </Form.Select>
                </div>

                <Table responsive hover className="admin-battles-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Название</th>
                            <th>Тип</th>
                            <th>Формат</th>
                            <th>Видимость</th>
                            <th>Статус</th>
                            <th>Участников</th>
                            <th>Дата начала</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {battles.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="text-center text-muted">
                                    Баттлов не найдено
                                </td>
                            </tr>
                        ) : (
                            battles.map((battle) => (
                                <tr key={battle.id}>
                                    <td>{battle.id}</td>
                                    <td>
                                        <strong>{battle.title}</strong>
                                        {battle.description && (
                                            <div className="text-muted small">
                                                {battle.description.substring(0, 50)}...
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <Badge bg={getTypeBadgeVariant(battle.type)}>
                                            {battle.type}
                                        </Badge>
                                    </td>
                                    <td>
                                        {battle.format && (
                                            <Badge bg={getFormatBadgeVariant(battle.format)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                {getFormatIcon(battle.format)} {battle.format}
                                            </Badge>
                                        )}
                                    </td>
                                    <td>
                                        <Badge bg={getVisibilityBadgeVariant(battle.visibility)}>
                                            {battle.visibility}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg={getStatusBadgeVariant(battle.status)}>
                                            {battle.status}
                                        </Badge>
                                    </td>
                                    <td>{battle.participantCount || 0}</td>
                                    <td>
                                        {battle.startTime 
                                            ? new Date(battle.startTime).toLocaleDateString('ru-RU')
                                            : '-'}
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-1"
                                            onClick={() => handleStatusChange(battle)}
                                            disabled={actionLoading}
                                        >
                                            Статус
                                        </Button>
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            className="me-1"
                                            as="a"
                                            href={`/admin/battles/${battle.id}/participants`}
                                        >
                                            Заявки
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteBattle(battle)}
                                            disabled={actionLoading}
                                        >
                                            Удалить
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* Модальное окно смены статуса */}
            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Изменение статуса баттла</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Баттл: <strong>{selectedBattle?.title}</strong>
                    </p>
                    <Form>
                        <Form.Group controlId="statusSelect">
                            <Form.Label>Новый статус</Form.Label>
                            <Form.Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                                <option value="PLANNED">PLANNED</option>
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="FINISHED">FINISHED</option>
                                <option value="CANCELLED">CANCELLED</option>
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
                        Отмена
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={saveStatus}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminBattles;
